import _ from 'lodash';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Grid, makeStyles} from '@material-ui/core';
import {Button, ButtonClass} from '@treez-ui/core/buttons/Button';
import {Stepper} from '@treez-ui/core/layout';
import {Title, TitleSize} from '@treez-ui/core/layout/Title';
import {ProductData} from '@treez/commons/sharedTypings/product';
import {ModalType, SnackbarType} from '@treez-ui/core/feedback/modals';
import {getDecimalPrecision} from '~/lib/numbers';
import {flattenProduct} from '~/lib/product';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';
import {
    selectMergeProduct,
    selectMergeProductsStorePresence,
} from '~/redux/selectors/mergeSelectors';
import {CatalogProductInformation, ProductStorePresenceAttributes} from '~/utils/ProductAPI';

import {CATALOG_ASSIGNMENT_DEFAULT_PRICE} from './CatalogAssignment';
import {MergeStep, STEPS} from './ProductMerge';
import {StoreAttributes} from '~/lib/directory';
import {getStoreProductValidationResult, StoreProductValidationResult} from '~/lib/errors';
import ProductValidationErrorMessage from '~/modules/widgets/products/ProductValidationErrorMessage';
import {
    openFeedbackModal,
    closeFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';

const {QUIET, PREFERRED} = ButtonClass;

const DECIMAL_PRECISION = 2;

interface Props {
    backToCatalog: () => void;
    catalogProductInformation: CatalogProductInformation;
    currentStep: number;
    handleMerge: () => void;
    nextStep: () => void;
    previousStep: () => void;
    requiredFields: ProductData[];
    setProductHasBeenSubmitted: (value: boolean) => void;
    stores: StoreAttributes[];
}

const ProductMergeNav = (props: Props) => {
    const {
        backToCatalog,
        catalogProductInformation,
        currentStep,
        handleMerge,
        nextStep,
        previousStep,
        requiredFields,
        setProductHasBeenSubmitted,
        stores,
    } = props;

    const dispatch = useDispatch();

    const productToMergeTo = useSelector(selectMergeProduct, _.isEqual);
    const storePresence = useSelector(selectMergeProductsStorePresence, _.isEqual);

    const canSkipCatalogAssignment = () => {
        const storePresenceCatalogIds: number[] = _.map(_.flatMap(_.values(storePresence)), 'id');

        if (_.isEmpty(storePresenceCatalogIds)) {
            return true;
        }

        return false;
    };

    const getSteps = () => {
        if (canSkipCatalogAssignment()) {
            return [MergeStep.MERGE_PRODUCT_DETAILS];
        }

        return STEPS;
    };

    const isProductToMergeToValid = () => {
        const currentInputs = flattenProduct(productToMergeTo);

        const requiredInputs = _.map(requiredFields, (field) => {
            return currentInputs[field];
        });

        if (_.includes(requiredInputs, undefined)) {
            return false;
        }

        // validate products region requirements
        const storePresenceCatalogIds: number[] = _.uniq(
            _.map(_.flatMap(_.values(storePresence)), 'id')
        );

        const storeProductValidationErrorResult: StoreProductValidationResult[] =
            getStoreProductValidationResult(
                productToMergeTo,
                storePresenceCatalogIds,
                stores,
                dispatch
            );

        if (storeProductValidationErrorResult.length > 0) {
            openFeedbackModal(closeFeedbackModal, `Not All Product Requirements Are Met`, {
                type: ModalType.ERROR,
                bodyElement: (
                    <>
                        {storeProductValidationErrorResult.map((validationResult, key) => (
                            <ProductValidationErrorMessage
                                key={key}
                                productValidationResult={validationResult.productValidationResult}
                                region={validationResult.region}
                                store={validationResult.store}
                            />
                        ))}
                    </>
                ),
            });
            return false;
        }

        return true;
    };

    const isCatalogProductInformationValid = () => {
        if (_.isEmpty(_.mapValues(catalogProductInformation))) {
            return true;
        }

        return _.every(_.mapValues(catalogProductInformation), isCatalogProductValid);
    };

    const isCatalogProductValid = (catalogProduct: Partial<ProductStorePresenceAttributes>) => {
        if (catalogProduct) {
            // valid if priceTierId is set
            if (catalogProduct.priceTierId) {
                return true;
            }

            // validates price format
            if (
                catalogProduct.price == null ||
                catalogProduct.price == undefined ||
                catalogProduct.price == CATALOG_ASSIGNMENT_DEFAULT_PRICE ||
                isNaN(Number(catalogProduct.price))
            ) {
                return false;
            }

            const precision = getDecimalPrecision(catalogProduct.price);

            if (precision > DECIMAL_PRECISION) {
                return false;
            }

            return true;
        }
        return false;
    };

    const isMergeValid = () => isProductToMergeToValid() && isCatalogProductInformationValid();

    const onClickMerge = () => {
        if (isMergeValid()) {
            handleMerge();
        }
    };

    const onMergeProductDetailSubmit = () => {
        setProductHasBeenSubmitted(true);
        if (isProductToMergeToValid()) {
            if (canSkipCatalogAssignment()) {
                onClickMerge();
            } else {
                nextStep();
            }
        } else {
            dispatch(
                showSnackbar({
                    content: `Product is missing required field(s). Check fields for additional information.`,
                    onClose: () => dispatch(clearSnackbar()),
                    type: SnackbarType.WARNING,
                })
            );
            console.error('Invalid productToMergeTo state.', productToMergeTo);
        }
    };

    const onSelectPriceSubmit = () => {
        if (isCatalogProductInformationValid()) {
            onClickMerge();
        } else {
            console.error('Invalid catalogProductInformation state.', catalogProductInformation);
        }
    };

    const renderStepper = () => {
        return (
            <Grid item sm={6}>
                <Stepper steps={getSteps()} activeStep={currentStep} />
            </Grid>
        );
    };
    const renderNav = () => {
        const progress = STEPS[currentStep];

        switch (progress) {
            case MergeStep.MERGE_PRODUCT_DETAILS:
                const submitText = canSkipCatalogAssignment() ? 'Merge' : 'Continue';

                return (
                    <Grid container item justifyContent="center" alignItems="center" sm={12}>
                        <Grid item sm={1}>
                            <Button buttonClass={QUIET} onClick={backToCatalog} text="Cancel" />
                        </Grid>
                        {renderStepper()}
                        <Grid item sm={1}>
                            <Button
                                buttonClass={PREFERRED}
                                onClick={onMergeProductDetailSubmit}
                                text={submitText}
                            />
                        </Grid>
                    </Grid>
                );
            case MergeStep.SELECT_PRICE:
                return (
                    <Grid container item justifyContent="center" alignItems="center" sm={12}>
                        <Grid item sm={1}>
                            <Button buttonClass={QUIET} onClick={previousStep} text="Back" />
                        </Grid>
                        {renderStepper()}
                        <Grid item sm={1}>
                            <Button
                                buttonClass={PREFERRED}
                                disabled={isCatalogProductInformationValid() === false}
                                onClick={onSelectPriceSubmit}
                                text="Merge"
                            />
                        </Grid>
                    </Grid>
                );
            default:
                return null;
        }
    };

    const useStyles = makeStyles({
        mergeNav: {
            padding: '0 40px',
        },
        mergeDetailsHeader: {
            height: '100px',
            padding: '20px 40px',
            boxSizing: 'border-box',
        },
    });

    const {mergeNav, mergeDetailsHeader} = useStyles();

    const newMergeProductTitle = `Merge to New Product`;

    const mergeToExistingProductTitle = `Merge to Existing Product: ${productToMergeTo.name}`;

    const mergeDetailsTitle = productToMergeTo.id
        ? mergeToExistingProductTitle
        : newMergeProductTitle;

    return (
        <Grid item container className={mergeNav} sm={12}>
            <Grid item container className={mergeDetailsHeader} sm={12}>
                <Title text={mergeDetailsTitle} size={TitleSize.LARGE} />
            </Grid>
            {renderNav()}
        </Grid>
    );
};

export default ProductMergeNav;
